/* Expansion sections — content added per CEO-review scope expansions.
 * Published criteria, alternatives comparison, founder bio.
 * Each section uses existing design tokens only (no new CSS).
 */

/* Published pass thresholds per trial (embedded in [trials.jsx] per tab). */
const VERDICT_CRITERIA = [
  {
    n: '01', name: 'Demand',
    rows: [
      { metric: 'Qualified response rate',     threshold: '≥ 35%',      logic: 'of outbound ICP contacts who meet screen', example: '127 of 340 → 37.3%',  verdict: 'PASS' },
      { metric: 'Stated willingness-to-pay',   threshold: '≥ €100/seat/mo', logic: 'unweighted median across qualified', example: '€290/seat (ops teams)', verdict: 'PASS' },
      { metric: 'Strategic buyer concentration', threshold: '≤ 40%',     logic: 'revenue share of top 3 buyer profiles', example: '31% · healthy spread', verdict: 'PASS' },
      { metric: 'Search-demand baseline',      threshold: '≥ 1,500/mo', logic: 'intent keywords, EU+UK, exact match',    example: '4,200/mo',             verdict: 'PASS' },
    ],
  },
  {
    n: '02', name: 'Retention',
    rows: [
      { metric: 'Week-1 return rate',       threshold: '≥ 50%',  logic: 'of activated users, any session',       example: '58%',       verdict: 'PASS' },
      { metric: 'Week-4 return rate',       threshold: '≥ 30%',  logic: 'of Week-1 returners',                   example: '40%',       verdict: 'PASS' },
      { metric: 'Core-action depth',        threshold: '≥ 3/wk', logic: 'median sessions with primary action',   example: '4.2/wk',    verdict: 'PASS' },
      { metric: 'Qualitative NPS (n≥30)',   threshold: '≥ +20',  logic: 'weighted by usage decile',              example: '+42',       verdict: 'PASS' },
    ],
  },
  {
    n: '03', name: 'Revenue',
    rows: [
      { metric: 'Paid conversion rate',     threshold: '≥ 8%',       logic: 'of activated users, 60-day window',    example: '12.1%',         verdict: 'PASS' },
      { metric: 'LTV / CAC ratio',          threshold: '≥ 3.0',      logic: '12-month projection, cohort-based',    example: '3.8 (€612 / €160)', verdict: 'CONDITIONAL' },
      { metric: 'Payback period',           threshold: '≤ 9 months', logic: 'gross margin basis',                   example: '7.2 months',    verdict: 'PASS' },
      { metric: 'Non-discount pricing hold',threshold: '100%',       logic: 'zero discounts offered during trial',  example: 'held · 42 seats', verdict: 'PASS' },
    ],
  },
];

/** Embedded in each Trial tab — metric + threshold only. */
function TrialCriteriaCompact({ trial }) {
  if (!trial?.rows?.length) return null;
  return (
    <div style={{ marginTop: 24, border: '1px solid var(--border)', background: 'var(--bg)' }}>
      <div style={{
        padding: '12px 18px',
        borderBottom: '1px solid var(--border)',
        fontSize: 10,
        letterSpacing: 3,
        color: 'var(--fg-dim)',
        fontWeight: 700,
        textTransform: 'uppercase',
      }}>
        Pass thresholds · Trial {trial.n} · all must pass
      </div>
      {trial.rows.map((r, i) => (
        <div
          key={r.metric}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 16,
            alignItems: 'baseline',
            padding: '12px 18px',
            borderBottom: i < trial.rows.length - 1 ? '1px solid var(--border)' : 'none',
            fontFamily: 'var(--mono)',
            fontSize: 12,
          }}
        >
          <span style={{ color: 'var(--fg)' }}>{r.metric}</span>
          <span style={{ color: 'var(--signal)', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.threshold}</span>
        </div>
      ))}
      <p className="small" style={{ margin: 0, padding: '10px 18px', color: 'var(--fg-dim)', letterSpacing: 0.5 }}>
        Thresholds fixed for your engagement · version on file · reviewed quarterly.
      </p>
    </div>
  );
}

/* =============================================================
 * AlternativesCompare — adjacent options vs 02100
 * =============================================================
 */
const COMPARE_ROWS = [
  { k: 'Duration',       ours: '6 months',  yc: '3 months',   spc: '5 months',    angels: 'open-ended', diy: '18–36 months' },
  { k: 'Cash out',       ours: '€95K flat', yc: '€125K USD',  spc: '—',           angels: '€500K–2M',   diy: '€200–650K burn' },
  { k: 'Equity taken',   ours: '12–18% · optional · Club election', yc: '7%',  spc: '—',           angels: '15–25% seed', diy: '0%' },
  { k: 'Verdict output', ours: 'published rubric', yc: 'demo day',  spc: 'peer cohort',  angels: 'pitch feedback', diy: 'your own opinion' },
  { k: 'Network',        ours: '100 operators · alumni squad', yc: '5,000+ alumni', spc: '~250 peers/yr', angels: 'investor rolodex', diy: '—' },
  { k: 'Who absorbs risk', ours: 'us, via equity', yc: 'you', spc: 'you',         angels: 'you + capital', diy: 'you alone' },
  { k: 'Best for',       ours: 'pre-product validation + build', yc: 'post-MVP growth',  spc: 'post-launch iteration', angels: 'scaling proven traction', diy: 'fully-formed theses' },
];

function AlternativesCompare() {
  return (
    <section id="comparison" data-screen-label="comparison">
      <div className="sec-head">
        <div className="sec-label">Comparison · adjacent options</div>
        <div>
          <h2>Why not YC?<br/>Why not <em>angels?</em><br/>Why not just build?</h2>
          <p className="lead dim" style={{marginTop: 28, maxWidth: '52ch'}}>
            Different instrument, different job. 02100 is a venture club for the 6 months before you raise or ship — where a fast no is cheaper than a slow yes. Use whichever fits your stage.
          </p>
        </div>
      </div>

      <div style={{overflowX: 'auto', border: '1px solid var(--border)', background: 'var(--surface)'}}>
        <table className="compare-table" style={{width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--mono)', fontSize: 12}}>
          <thead>
            <tr style={{borderBottom: '1px solid var(--border)'}}>
              <th style={compareTh({w: '180px'})}>&nbsp;</th>
              <th style={compareTh({signal: true})}>02100</th>
              <th style={compareTh()}>YC</th>
              <th style={compareTh()}>SPC</th>
              <th style={compareTh()}>Angels</th>
              <th style={compareTh()}>DIY</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((row, i) => (
              <tr key={i} style={{borderBottom: i < COMPARE_ROWS.length - 1 ? '1px solid var(--border)' : 'none'}}>
                <td style={compareTd({dim: true, upper: true, key: true})}>{row.k}</td>
                <td style={compareTd({signal: true})}>{row.ours}</td>
                <td style={compareTd()}>{row.yc}</td>
                <td style={compareTd()}>{row.spc}</td>
                <td style={compareTd()}>{row.angels}</td>
                <td style={compareTd()}>{row.diy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{marginTop: 20, display: 'grid', gridTemplateColumns: '1fr auto', gap: 24, alignItems: 'center', padding: 20, border: '1px solid var(--border)', background: 'var(--bg)'}}>
        <p className="small" style={{color: 'var(--fg)', margin: 0, maxWidth: 'none'}}>
          Pick the instrument that fits the question. <b style={{color: 'var(--signal)'}}>If the question is "is this worth building at all?"</b> — that is the one we answer.
        </p>
        <span className="stamp" style={{transform: 'rotate(-2deg)'}}>Different job</span>
      </div>
    </section>
  );
}

function compareTh({w, signal} = {}) {
  return {
    fontSize: 10, letterSpacing: 3, fontWeight: 700,
    color: signal ? 'var(--signal)' : 'var(--fg-dim)',
    textTransform: 'uppercase', textAlign: 'left',
    padding: '16px 18px', width: w,
    borderLeft: signal ? '2px solid var(--signal)' : undefined,
    background: signal ? 'var(--bg)' : undefined,
  };
}
function compareTd({signal, dim, upper, key} = {}) {
  return {
    padding: '14px 18px',
    color: signal ? 'var(--fg-bright)' : dim ? 'var(--fg-dim)' : 'var(--fg)',
    fontWeight: signal ? 700 : 400,
    fontSize: key ? 10 : 12,
    letterSpacing: upper ? 2 : 0,
    textTransform: upper ? 'uppercase' : 'none',
    verticalAlign: 'top',
    borderLeft: signal ? '2px solid var(--signal)' : undefined,
    background: signal ? 'var(--bg)' : undefined,
    fontVariantNumeric: 'tabular-nums',
  };
}

/* =============================================================
 * FounderSection — Sasha Maxim.
 * Placement: after AlternativesCompare, before Pricing.
 * Portrait: "SM" monogram seal (ASCII) — DESIGN.md-compliant
 * fallback for "grayscale halftone photo" treatment.
 * =============================================================
 */
const FOUNDER_MONOGRAM = `\
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
░░░██████╗░░░░░░██╗░██╗░░██╗░░░░░░░░░
░░██╔════╝░░░░░░██║░██║░██╔╝░░░░░░░░░
░░╚█████╗░░░░░░░██║░█████╔╝░░░░░░░░░░
░░░╚═══██╗░░░░░░██║░██╔═██╗░░░░░░░░░░
░░██████╔╝░░░░░░██║░██║░░██╗░░░░░░░░░
░░╚═════╝░░░░░░░╚═╝░╚═╝░░╚═╝░░░░░░░░░
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
░░▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓░░
░░░ MBR-000 · FOUNDER · 02100.CLUB ░░
░░▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓░░
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░`;

const FOUNDER_CREDS = [
  { year: '2011–16', role: 'Product lead · EU B2B (exited)',        outcome: '0 → 1.1M users · acq. undisclosed' },
  { year: '2018–22', role: 'Founder · hardware + software',         outcome: 'Shutdown — pattern that shaped 02100' },
  { year: '2026', role: 'Founder · 02100.club', outcome: 'In publication' },
];

function FounderSection() {
  return (
    <section id="founder" data-screen-label="founder">
      <div className="sec-head">
        <div className="sec-label">Founder · the person behind the stance</div>
        <div>
          <h2>Sasha Maxim.<br/>Built it <em>before</em> preaching it.</h2>
        </div>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 48, alignItems: 'start'}} className="founder-grid">
        <figure style={{margin: 0}}>
          <div className="halftone" style={{padding: 20, border: '1px solid var(--border)'}}>
            <pre
              className="ascii"
              aria-hidden="true"
              style={{color: 'var(--signal)', fontSize: 9, lineHeight: 1.05, margin: 0, textAlign: 'center'}}
            >
{FOUNDER_MONOGRAM}
            </pre>
          </div>
          <figcaption className="small" style={{marginTop: 12, color: 'var(--fg-dim)', letterSpacing: 2, textTransform: 'uppercase'}}>
            SM · founder seal · member 000
          </figcaption>
        </figure>

        <div>
          <p className="large">
            &ldquo;I shipped things nobody paid for long enough to know the difference between conviction and evidence.<br/>
            02100 exists so <em>you get the receipts before the cap table locks</em>.&rdquo;
          </p>

          <div style={{marginTop: 32, borderTop: '1px solid var(--border)'}}>
            {FOUNDER_CREDS.map((c, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: 16,
                padding: '14px 0', borderBottom: '1px solid var(--border)',
                fontFamily: 'var(--mono)', fontSize: 12,
                fontVariantNumeric: 'tabular-nums',
              }}>
                <span style={{color: 'var(--signal)', fontWeight: 700, letterSpacing: 2}}>{c.year}</span>
                <span style={{color: 'var(--fg)'}}>{c.role}</span>
                <span style={{color: 'var(--fg-dim)', textAlign: 'right'}}>{c.outcome}</span>
              </div>
            ))}
          </div>

          <p className="dim" style={{marginTop: 24, fontSize: 13, fontFamily: 'var(--mono)'}}>
            Receipts over titles. The credential that matters is the one that shipped.
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .founder-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

export { VERDICT_CRITERIA, TrialCriteriaCompact, AlternativesCompare, FounderSection };
